import { Checkbox, Select, DatePicker, Tag } from 'antd';
import { useCountryArray } from '../../../utils/countryArray';
import { useDateManagement } from '../utils/dateManagement';
import { translateAnimalsTypes } from '../utils/translateAnimalsTypes';
import { useSupervisionContext } from '../../../contexts/SupervisionContext/SupervisionContext';
import { createPhoneNumberFieldConfig } from '../../../utils/formFields/phoneNumberField';

const { Option } = Select;

/**
 * Custom hook for generating main information fields.
 *
 * @param {Object} options - Options for generating main information fields.
 * @param {Object} options.enums - Enums for dropdown options.
 * @param {Function} options.t - Translation function.
 * @param {boolean} options.isFieldsLoading - Loading state of fields.
 * @param {Function} options.setOwnerId - Function to set owner id.
 * @param {Array} options.owners - List of owners.
 * @param {Function} options.setStartDate - Function to set start date.
 * @param {Function} options.setEndDate - Function to set end date.
 * @param {Function} options.setAnimals - Function to set animals.
 * @param {Object} options.owner - Selected owner object.
 * @returns {Object} An object containing the main information fields.
 */
export const useMainInfoFields = ({
  enums,
  t,
  isFieldsLoading,
  owners,
  startDate,
  endDate,
  animalsRef,
  owner,
  userRole
}) => {
  const countries = useCountryArray();
  const { setOwnerId } = useSupervisionContext();

  const startDateRef = startDate;
  const endDateRef = endDate;
  const animals = animalsRef;

  const { dateChangeAction, disabledDateAction, cellRenderer } =
    useDateManagement({
      startDate: startDateRef,
      endDate: endDateRef
    });

  const filterOption = (input, option) => {
    const children = String(option?.children);
    return children.toLowerCase().includes(input.toLowerCase());
  };

  const handleChangeAnimals = (values, options) => {
    const selectedAnimalObjects =
      values.length > 0 ? options.map((option) => option.animal) : [];
    animals.current = selectedAnimalObjects;
  };

  const informationsFields = [
    {
      name: ['main_informations', 'owner'],
      label: 'owner_reference',
      rules: userRole === 'admins:ADMIN' ? [{ required: true }] : undefined,
      input:
        userRole === 'users:OWNER' ? (
          <Select disabled loading={isFieldsLoading}>
            {Object.keys(owner).length ? (
              <Option value={owner._id} key={owner._id}>
                {owner.reference}
              </Option>
            ) : null}
          </Select>
        ) : (
          <Select
            showSearch
            optionFilterProp="children"
            filterOption={filterOption}
            onChange={(e, option) => setOwnerId(option.owner._id)}
            loading={isFieldsLoading}
          >
            {owners?.map((item) => (
              <Option value={item._id} key={item._id} owner={item}>
                {item.reference}
              </Option>
            ))}
          </Select>
        )
    },
    {
      name: ['main_informations', 'owner_name'],
      label: 'owner.name',
      input:
        userRole === 'users:OWNER' ? (
          <Select disabled loading={isFieldsLoading}>
            {Object.keys(owner).length ? (
              <Option value={owner._id} key={owner._id}>
                {owner?.first_name} {owner?.last_name}
              </Option>
            ) : null}
          </Select>
        ) : (
          <Select
            loading={isFieldsLoading}
            showSearch
            filterOption={filterOption}
            onChange={(e, option) => setOwnerId(option.owner._id)}
          >
            {owners?.map((item) => (
              <Option value={item._id} key={item._id} owner={item}>
                {item?.first_name} {item?.last_name}
              </Option>
            ))}
          </Select>
        )
    },
    {
      name: ['main_informations', 'start_date'],
      input: (
        <DatePicker
          format="DD/MM/YYYY"
          onChange={dateChangeAction.startDate}
          disabledDate={disabledDateAction.startDate}
          cellRender={(current, info) =>
            cellRenderer(current, 'startDate', info)
          }
        />
      ),
      rules: [{ required: true }]
    },
    {
      name: ['main_informations', 'end_date'],
      input: (
        <DatePicker
          format="DD/MM/YYYY"
          onChange={dateChangeAction.endDate}
          disabledDate={disabledDateAction.endDate}
          cellRender={(current, info) => cellRenderer(current, 'endDate', info)}
        />
      ),
      rules: [{ required: true }]
    },
    {
      name: ['main_informations', 'animals'],
      input: (
        <Select
          loading={isFieldsLoading}
          showSearch
          filterOption={filterOption}
          onChange={handleChangeAnimals}
          mode="multiple"
        >
          {owner?.animals?.map(
            (animal) =>
              !animal?.is_archived && (
                <Option value={animal._id} key={animal._id} animal={animal}>
                  {animal.name || translateAnimalsTypes(t)[animal.type]}
                </Option>
              )
          )}
        </Select>
      ),
      rules: [{ required: true }]
    },
    {
      name: ['main_informations', 'veterinarian', 'name']
    },
    createPhoneNumberFieldConfig('phone_number', [
      'main_informations',
      'veterinarian'
    ]),
    {
      name: ['main_informations', 'veterinarian', 'address', 'number']
    },
    {
      name: ['main_informations', 'veterinarian', 'address', 'street']
    },
    {
      name: ['main_informations', 'veterinarian', 'address', 'postal_code']
    },
    {
      name: ['main_informations', 'veterinarian', 'address', 'city']
    },
    {
      name: ['main_informations', 'veterinarian', 'address', 'country'],
      input: (
        <Select
          showSearch
          filterOption={(input, option) =>
            option.value.toLowerCase().includes(input.toLowerCase())
          }
        >
          {(countries || []).map((country) => (
            <Option key={country} value={country}>
              <Tag>{country}</Tag>
            </Option>
          ))}
        </Select>
      )
    },
    {
      name: ['main_informations', 'accept'],
      input: (
        <Select
          loading={isFieldsLoading}
          mode="multiple"
          showSearch
          filterOption={filterOption}
        >
          {enums?.pensioner_type?.map((type) => (
            <Option value={type} key={type}>
              {t(`supervisions.form.tags.${type}`)}
            </Option>
          ))}
        </Select>
      )
    },
    {
      name: ['main_informations', 'accept_smoker'],
      input: <Checkbox />,
      valuePropName: 'checked'
    }
  ];

  return { informationsFields };
};
